import { Button, IconButton, Input, Radio, Switch, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import LanguageSelector from '../components/LanguageSelector'
import { useFontSize } from '../contexts/FontSizeContext'
import { useSessionContext } from '../contexts/SessionContext'
import { useUserPreferences } from '../contexts/UserPreferencesContext'
import { useFeatureAuth } from '../hooks/authorization'
import { usePageTitle } from '../hooks/toolkit'
import { FEATURES } from '../utils/constants/features.constants'
import { useDarkMode } from '../contexts/DarkModeContext'

const Profile = () => {
  const { t } = useTranslation()
  usePageTitle('Profile | Agami')
  const { user } = useSessionContext()
  const advMapStylesAuth = useFeatureAuth(FEATURES.ADVANCED_MAP_STYLES)
  const { mapStyle, setMapStyle, apply3DMap, setApply3DMap } = useUserPreferences()
  const { fontSize, increase, decrease, reset } = useFontSize()
  const { darkMode } = useDarkMode()

  return (
    <div className="w-11/12 mx-auto mt-12 pb-10">
      <Typography variant="h1" className="text-primary dark:text-base-gray-400" data-testid="profile-title">
        {t('profile.title')}
      </Typography>

      <div className="grid grid-cols-2 gap-6 pt-10 w-full md:w-1/2">
        {/* Infos utilisateur */}
        <div className="col-span-2">
          <Input
            value={user?.email}
            size="lg"
            readOnly
            type="email"
            label={t('profile.email')}
            data-testid="profile-email-input"
            {...(darkMode && { color: 'white' })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            value={user?.name || ''}
            size="lg"
            readOnly
            type="text"
            label={t('profile.name')}
            data-testid="profile-name-input"
            {...(darkMode && { color: 'white' })}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            value={user?.surname || ''}
            size="lg"
            readOnly
            type="text"
            label={t('profile.surname')}
            data-testid="profile-surname-input"
            {...(darkMode && { color: 'white' })}
          />
        </div>
        <div className="col-span-2">
          <Input
            value={user?.phone || ''}
            size="lg"
            readOnly
            type="phone"
            label={t('profile.phone')}
            data-testid="profile-phone-input"
            {...(darkMode && { color: 'white' })}
          />
        </div>
        <div className="col-span-2">
          <Input
            value={user?.company || ''}
            size="lg"
            readOnly
            type="text"
            label={t('profile.company')}
            data-testid="profile-company-input"
            {...(darkMode && { color: 'white' })}
          />
        </div>
        <div className="col-span-2">
          <Input
            value={user?.bch_model || ''}
            size="lg"
            readOnly
            type="text"
            label={t('profile.yourBch')}
            data-testid="profile-bch-input"
            {...(darkMode && { color: 'white' })}
          />
        </div>
      </div>

      <hr className="w-full my-8 opacity-10 border-primary dark:border-white" />

      <div className="mt-2">
        <Typography variant="h3" className="text-primary mb-4 dark:text-base-gray-400">
          {t('profile.languagePreferences')}
        </Typography>
        <div className="mb-6 w-full md:w-1/2">
          <LanguageSelector
            className="w-full"
            size="lg"
            variant="outlined"
            testId="profile-language-select"
          />
        </div>
      </div>

      <hr className="w-full my-8 opacity-10 border-primary dark:border-white" />

      {/* TEXT SIZE */}
      <div className="mt-2">
        <Typography variant="h3" className="text-primary mb-4 dark:text-base-gray-400">
          {t('profile.textSizeSettings')}
        </Typography>

        <div className="flex flex-wrap items-center gap-3">
          <IconButton
            color={darkMode ? 'white' : ('primary' as colors)}
            size="sm"
            variant="outlined"
            onClick={decrease}
            data-testid="profile-text-decrease"
          >
            -
          </IconButton>

          <Typography
            className="text-primary text-sm font-medium dark:text-gray-200"
            data-testid="profile-text-size-value"
          >
            {fontSize}px
          </Typography>

          <IconButton
            color={darkMode ? 'white' : ('primary' as colors)}
            size="sm"
            variant="outlined"
            onClick={increase}
            data-testid="profile-text-increase"
          >
            +
          </IconButton>

          <Button
            color={darkMode ? 'white' : ('primary' as colors)}
            size="sm"
            variant="outlined"
            onClick={reset}
            data-testid="profile-text-reset"
          >
            {t('profile.reset')}
          </Button>
        </div>
      </div>

      <hr className="w-full my-8 opacity-10 border-primary dark:border-white" />

      {/* MAP SETTINGS */}
      {advMapStylesAuth && (
        <div className="mt-8" data-testid="profile-map-settings">
          <Typography variant="h3" className="text-primary dark:text-base-gray-400">
            {t('profile.mapsSettings')}
          </Typography>
          <div className="flex gap-1 justify-start items-center mt-2">
            <Typography className="font-bold text-primary dark:text-white">
              {t('profile.mapStyle')}
            </Typography>
            <div className="flex gap-1 justify-center items-center">
              <Radio
                type="radio"
                name="map-style"
                label={t('profile.vector')}
                value="vector"
                checked={mapStyle === 'vector'}
                onChange={() => setMapStyle('vector')}
                data-testid="profile-map-style-vector"
                labelProps={{
                  className: 'text-gray-600 dark:!text-base-gray-500',
                }}
              />
              <Radio
                type="radio"
                name="map-style"
                label={t('profile.satellite')}
                value="satellite"
                checked={mapStyle === 'satellite'}
                onChange={() => setMapStyle('satellite')}
                data-testid="profile-map-style-satellite"
                labelProps={{
                  className: 'text-gray-600 dark:!text-base-gray-500',
                }}
              />
            </div>
          </div>
          <div className="flex gap-4 justify-start items-center mt-2">
            <Typography className="font-bold text-primary dark:text-gray-200">
              {t('profile.threeDMaps')}
            </Typography>
            <Switch
              color={(darkMode ? 'accent' : 'primary') as colors}
              checked={apply3DMap}
              onChange={(e) => setApply3DMap(e.currentTarget.checked)}
              data-testid="profile-map-3d-toggle"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Profile
